'use client'

import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { cx } from 'utils/cx'

import { useFamilies } from '../../contexts/FamiliesProvider/FamiliesProvider'
import { getClientHow2Measure } from '../../services/getHow2Measure/client/getClientHow2Measure'
import type { SizeGuideHow2Measure } from '../../types/SizeGuide.types'

import styles from './How2Measure.module.scss'
import text from 'fukku/styles/texts.module.scss'

interface HowToMeasureProps {
	readonly initialHow2Measure: SizeGuideHow2Measure[]
	readonly className?: string
}

export function HowToMeasure({
	initialHow2Measure,
	className,
}: HowToMeasureProps): React.ReactNode {
	const { t } = useLabels()
	const { selectedFamilyId } = useFamilies()
	const {
		country: { languageISO },
	} = useMasterData()

	const [how2Measure, setHow2Measure] = useState(initialHow2Measure)
	const firstRenderRef = useRef(true)

	useEffect(() => {
		const fetchHowToMeasure = async () => {
			const { how2Measure: fetchedHow2Measure } = await getClientHow2Measure({
				languageISO,
				familyId: selectedFamilyId,
			})

			setHow2Measure(fetchedHow2Measure)
		}

		if (!firstRenderRef.current) {
			fetchHowToMeasure()
		} else {
			firstRenderRef.current = false
		}
	}, [selectedFamilyId])

	return (
		!!how2Measure.length && (
			<div
				className={cx(styles.how2Measure, className)}
				data-testid='size-guide-how2Measure'
			>
				<h2 className={text.titleL}>{t('sizeGuide.howToMeasure.title')}</h2>
				<div className={styles.how2MeasureCarousel}>
					{how2Measure.map((measure) => (
						<div className={styles.card} key={measure.name}>
							<div className={styles.imageWrapper}>
								<Image
									src={measure.image}
									alt={measure.description}
									key={measure.name}
									sizes='132px'
									fill
								/>
							</div>
							<p className={text.bodyMRegular}>{measure.name}</p>
							<p>{measure.description}</p>
						</div>
					))}
				</div>
			</div>
		)
	)
}
